import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ─── useSpectralMarkers ───────────────────────────────────────────────────────
// Headless, rendering-agnostic model for the FFT spectral-analysis tools:
// harmonic series, standalone markers, sidebands, and the read cursor. It owns
// the marker list, the keyboard placement wizard state machine, the CPM spectral
// math (harmonic multiples / sideband spacing), and the amplitude readouts.
//
// It draws NOTHING and knows no pixels. Consumers supply:
//   • ampLookup(cpm)  — returns the per-trace amplitude readout at a frequency
//   • binRes          — CPM per bin (arrow-key step + clamp granularity)
//   • xMax            — upper CPM clamp for cursor/placement
//   • active          — gate keyboard handling (e.g. the FFT tab is showing)
// and render the emitted CPM-space line specs however they like (canvas 2D or
// Recharts <ReferenceLine>), mapping cpm→x with their own scale.
//
// Auto-1×-RPM overlay and drag-to-zoom deliberately stay OUT of this hook — they
// are WaveformViewer-local (showRevMarkers, xMin/xMaxCpm).

export type SpectralMarkerType = 'standalone' | 'harmonic' | 'sideband';

export interface SpectralMarker {
  id: string;
  type: SpectralMarkerType;
  cpm: number;          // anchor frequency (fundamental / centre / the point)
  label: string;
  colour: string;
  // harmonic
  fundamental?: number;
  count?: number;
  // sideband
  centre?: number;
  spacing?: number;
  sbCount?: number;
}

export interface SpectralPlacement {
  markerType: SpectralMarkerType;
  step: 'position' | 'spacing' | 'count';
  cursorCpm: number;
  fundamentalCpm?: number;
  spacing: number;
  count: number;
}

// One vertical line the consumer draws, in CPM. `role`/`glyph` carry the styling
// intent so canvas and SVG renderers can match the original look.
export interface SpectralLine {
  cpm: number;
  label: string;
  colour: string;
  dashed: boolean;
  role: 'standalone' | 'centre' | 'harmonic' | 'sideband';
  glyph?: 'triangle';
}

export interface AmpSample { label: string; amp: string; colour: string; }
export type AmpLookup = (cpm: number) => AmpSample[];

export interface UseSpectralMarkersOptions {
  ampLookup: AmpLookup;
  binRes: number;
  xMax: number;
  active: boolean;
}

export interface SpectralMarkersApi {
  // state
  markers: SpectralMarker[];
  placement: SpectralPlacement | null;
  cursorCpm: number | null;
  contextMenu: { x: number; y: number; cpm: number } | null;
  // rendering-agnostic geometry (CPM space)
  markerLines: SpectralLine[];
  previewLines: SpectralLine[];        // faded lines shown while placing
  placementCursorCpm: number | null;   // the bold placement cursor (1× / centre)
  // readouts (via injected ampLookup)
  cursorReadout: AmpSample[] | null;
  placementReadout: { cpm: number; axes: AmpSample[] } | null;
  sidebandReadout: { label: string; cpm: number; axes: AmpSample[] }[] | null;
  // actions
  startPlacement: (type: SpectralMarkerType, cpm: number) => void;
  cancelPlacement: () => void;
  removeMarker: (id: string) => void;
  removeLast: () => void;
  clearAll: () => void;
  setCursor: (cpm: number | null) => void;
  openContextMenu: (x: number, y: number, cpm: number) => void;
  closeContextMenu: () => void;
}

export const PLACEMENT_COLOUR: Record<SpectralMarkerType, string> = {
  standalone: '#7c3aed',
  harmonic: '#dc2626',
  sideband: '#d97706',
};

let _idSeq = 0;
const uid = () => `sm_${Date.now().toString(36)}_${(_idSeq++).toString(36)}`;

// Shared keyboard-focus token: when several useSpectralMarkers instances are
// mounted (e.g. the Compare panel + the WaveformViewer FFT tab), only the one
// that most recently became active (hovered / placing) handles key events, so a
// placement on one chart can't also nudge the other. When no instance is active
// the token is null and each falls back to its own `active` gate.
let _focusOwner: object | null = null;

// ─── Pure CPM spectral math (shared by every renderer) ─────────────────────────
// Expand a saved marker into its concrete vertical lines. This is the single
// implementation of harmonic-multiple and sideband-spacing geometry.
export function expandMarkerToLines(m: SpectralMarker): SpectralLine[] {
  if (m.type === 'standalone') {
    return [{ cpm: m.cpm, label: m.label, colour: m.colour, dashed: false, role: 'standalone' }];
  }
  if (m.type === 'harmonic' && m.fundamental && m.count) {
    const out: SpectralLine[] = [];
    for (let h = 1; h <= m.count; h++) {
      out.push({ cpm: m.fundamental * h, label: `h${h}`, colour: m.colour, dashed: true, role: 'harmonic', glyph: 'triangle' });
    }
    return out;
  }
  if (m.type === 'sideband' && m.centre && m.spacing && m.sbCount) {
    const out: SpectralLine[] = [
      { cpm: m.centre, label: m.label || `${Math.round(m.centre / 1000)}k`, colour: m.colour, dashed: false, role: 'centre' },
    ];
    for (let s = 1; s <= m.sbCount; s++) {
      out.push({ cpm: m.centre + m.spacing * s, label: `+${s}`, colour: m.colour, dashed: true, role: 'sideband' });
      out.push({ cpm: m.centre - m.spacing * s, label: `-${s}`, colour: m.colour, dashed: true, role: 'sideband' });
    }
    return out;
  }
  return [];
}

export function useSpectralMarkers(opts: UseSpectralMarkersOptions): SpectralMarkersApi {
  const { ampLookup, binRes, xMax, active } = opts;

  const [markers, setMarkers] = useState<SpectralMarker[]>([]);
  const [placement, setPlacement] = useState<SpectralPlacement | null>(null);
  const [cursorCpm, setCursorCpm] = useState<number | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; cpm: number } | null>(null);

  // Stable per-instance identity for the shared keyboard-focus token.
  const idRef = useRef<object>({});
  useEffect(() => {
    if (active) { _focusOwner = idRef.current; }
    else if (_focusOwner === idRef.current) { _focusOwner = null; }
  }, [active]);

  // Refs so the once-installed keydown listener always reads fresh values.
  const activeRef = useRef(active); activeRef.current = active;
  const binResRef = useRef(binRes); binResRef.current = binRes;
  const xMaxRef = useRef(xMax); xMaxRef.current = xMax;
  const placementRef = useRef(placement); placementRef.current = placement;

  const clamp = (cpm: number) => Math.max(0, Math.min(xMaxRef.current, cpm));

  const startPlacement = useCallback((type: SpectralMarkerType, cpm: number) => {
    setPlacement({ markerType: type, step: 'position', cursorCpm: cpm, spacing: 1000, count: 10 });
    setContextMenu(null);
  }, []);
  const cancelPlacement = useCallback(() => setPlacement(null), []);
  const removeMarker = useCallback((id: string) => setMarkers(prev => prev.filter(x => x.id !== id)), []);
  const removeLast = useCallback(() => setMarkers(prev => prev.slice(0, -1)), []);
  const clearAll = useCallback(() => setMarkers([]), []);
  const setCursor = useCallback((cpm: number | null) => setCursorCpm(cpm), []);
  const openContextMenu = useCallback((x: number, y: number, cpm: number) => setContextMenu({ x, y, cpm }), []);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // ─── Keyboard placement wizard (self-contained; gated on `active`) ───────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Only the focus owner handles keys (null owner → single-instance fallback).
      if (_focusOwner !== null && _focusOwner !== idRef.current) return;
      const pm = placementRef.current;

      // Escape cancels an in-progress placement / closes the menu regardless of
      // which tab is active (matches the original single-handler behaviour).
      if (e.key === 'Escape') {
        if (pm) { setPlacement(null); e.preventDefault(); }
        setContextMenu(null);
        return;
      }

      // Arrow/Enter placement navigation only while this spectrum is showing.
      if (!activeRef.current) return;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const res = binResRef.current || 60;
        const step = res * (e.shiftKey ? 50 : 1);
        e.preventDefault();
        if (pm) {
          if (pm.step === 'position') {
            setPlacement(p => (p ? { ...p, cursorCpm: clamp(p.cursorCpm + dir * step) } : p));
          } else if (pm.step === 'spacing') {
            setPlacement(p => (p ? { ...p, spacing: Math.max(res, p.spacing + dir * step) } : p));
          } else if (pm.step === 'count') {
            setPlacement(p => (p ? { ...p, count: Math.max(1, Math.min(20, p.count + dir)) } : p));
          }
        } else {
          setCursorCpm(prev => clamp((prev ?? 0) + dir * step));
        }
        return;
      }

      if (e.key === 'Enter' && pm) {
        e.preventDefault();
        if (pm.step === 'position') {
          if (pm.markerType === 'standalone') {
            setMarkers(prev => [...prev, { id: uid(), type: 'standalone', cpm: pm.cursorCpm, label: Math.round(pm.cursorCpm).toLocaleString(), colour: PLACEMENT_COLOUR.standalone }]);
            setPlacement(null);
          } else if (pm.markerType === 'harmonic') {
            setPlacement(p => (p ? { ...p, fundamentalCpm: p.cursorCpm, step: 'count' } : p));
          } else if (pm.markerType === 'sideband') {
            setPlacement(p => (p ? { ...p, fundamentalCpm: p.cursorCpm, step: 'spacing' } : p));
          }
        } else if (pm.step === 'spacing') {
          setPlacement(p => (p ? { ...p, step: 'count' } : p));
        } else if (pm.step === 'count') {
          const fund = pm.fundamentalCpm!;
          if (pm.markerType === 'harmonic') {
            setMarkers(prev => [...prev, { id: uid(), type: 'harmonic', cpm: fund, fundamental: fund, count: pm.count, label: `${Math.round(fund / 1000)}k`, colour: PLACEMENT_COLOUR.harmonic }]);
          } else {
            setMarkers(prev => [...prev, { id: uid(), type: 'sideband', cpm: fund, centre: fund, spacing: pm.spacing, sbCount: pm.count, label: 'SB', colour: PLACEMENT_COLOUR.sideband }]);
          }
          setPlacement(null);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Geometry + readouts ─────────────────────────────────────────────────────
  const markerLines = useMemo(() => markers.flatMap(expandMarkerToLines), [markers]);

  // Faded preview during placement (harmonic multiples 2..N / sideband ±s), plus
  // the bold 1×/centre cursor — same geometry the WaveformViewer canvas draws.
  const placementCursorCpm = placement
    ? (placement.step === 'position' ? placement.cursorCpm : (placement.fundamentalCpm ?? null))
    : null;
  const previewLines = useMemo<SpectralLine[]>(() => {
    if (!placement) return [];
    const colour = PLACEMENT_COLOUR[placement.markerType];
    const out: SpectralLine[] = [];
    if (placement.markerType === 'harmonic') {
      const fund = placement.step === 'count' ? (placement.fundamentalCpm ?? placement.cursorCpm) : placement.cursorCpm;
      for (let h = 2; h <= placement.count; h++) out.push({ cpm: fund * h, label: `h${h}`, colour, dashed: true, role: 'harmonic' });
    } else if (placement.markerType === 'sideband') {
      const fund = placement.step === 'position' ? placement.cursorCpm : (placement.fundamentalCpm ?? placement.cursorCpm);
      const cnt = placement.step === 'count' ? placement.count : 3;
      for (let s = 1; s <= cnt; s++) {
        out.push({ cpm: fund + placement.spacing * s, label: `+${s}`, colour, dashed: true, role: 'sideband' });
        out.push({ cpm: fund - placement.spacing * s, label: `-${s}`, colour, dashed: true, role: 'sideband' });
      }
    }
    return out;
  }, [placement]);

  const cursorReadout = useMemo(
    () => (cursorCpm === null ? null : ampLookup(cursorCpm)),
    [cursorCpm, ampLookup],
  );

  const placementReadout = useMemo(() => {
    if (!placement || placement.step !== 'position') return null;
    return { cpm: placement.cursorCpm, axes: ampLookup(placement.cursorCpm) };
  }, [placement, ampLookup]);

  const sidebandReadout = useMemo(() => {
    if (!placement || placement.markerType !== 'sideband' || (placement.step !== 'position' && placement.step !== 'spacing')) return null;
    const fund = placement.step === 'position' ? placement.cursorCpm : placement.fundamentalCpm!;
    const spacing = placement.spacing;
    return [
      { label: '−1×', cpm: fund - spacing },
      { label: 'Fund', cpm: fund },
      { label: '+1×', cpm: fund + spacing },
    ].map(pos => ({ label: pos.label, cpm: pos.cpm, axes: ampLookup(Math.max(0, pos.cpm)) }));
  }, [placement, ampLookup]);

  return {
    markers, placement, cursorCpm, contextMenu,
    markerLines, previewLines, placementCursorCpm,
    cursorReadout, placementReadout, sidebandReadout,
    startPlacement, cancelPlacement, removeMarker, removeLast, clearAll,
    setCursor, openContextMenu, closeContextMenu,
  };
}
